import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useReducer,
  useRef,
  useState,
} from 'react';
import {
  defaultConfig,
  fitConfigToSession,
  hasCoachData,
} from '../model/defaults';
import { draftReducer, type DraftAction } from '../model/draftReducer';
import type { MatchingConfig, MatchingSessionState } from '../model/types';
import type { ConnectionStatus } from './useMatchingSession';

export type SaveState = 'saved' | 'saving' | 'offline';

/** Wait this long after the last edit before sending the config. */
const SAVE_DEBOUNCE_MS = 500;

interface UseMatchingDraftOptions {
  session: MatchingSessionState;
  send: (method: string, arg?: unknown) => boolean;
  connection: ConnectionStatus;
  requestNumber?: number;
}

/**
 * The editable `MatchingConfig`. Seeded from `session.config` (or defaults
 * for a session nobody has edited). Every edit re-renders at once and is sent
 * with `set_config` after a short pause; `flush()` sends right away (step
 * changes, before Start).
 *
 * Leaving the page sends any unsent edits: on unmount (from a layout-effect
 * cleanup, which runs while the socket is still open) and, best effort, on
 * `pagehide` for a reload or a closed tab.
 *
 * matching-server echoes the config to every connected admin. Our own echo
 * marks the draft saved. Someone else's replaces the draft when we have no
 * unsent edits; otherwise our edits win and `remoteChanged` is set so the UI
 * can say so.
 */
export function useMatchingDraft({
  session,
  send,
  connection,
  requestNumber = 1,
}: UseMatchingDraftOptions) {
  const [config, dispatch] = useReducer(draftReducer, null, () =>
    session.config
      ? fitConfigToSession(session.config, hasCoachData(session))
      : defaultConfig(session, requestNumber)
  );
  const [saveState, setSaveState] = useState<SaveState>('saved');
  const [remoteChanged, setRemoteChanged] = useState(false);

  const configRef = useRef<MatchingConfig>(config);
  const pendingRef = useRef(false);
  const lastSentRef = useRef<string | null>(
    session.config ? JSON.stringify(session.config) : null
  );
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // A layout effect, so the ref is current before any cleanup or pagehide.
  useLayoutEffect(() => {
    configRef.current = config;
  }, [config]);

  const flush = useCallback(
    (force = false): boolean => {
      clearTimeout(timerRef.current);
      if (!pendingRef.current && !force) return true;
      const json = JSON.stringify(configRef.current);
      if (!send('set_config', configRef.current)) {
        setSaveState('offline');
        return false;
      }
      lastSentRef.current = json;
      pendingRef.current = false;
      return true;
    },
    [send]
  );

  const update = useCallback(
    (action: DraftAction) => {
      dispatch(action);
      pendingRef.current = true;
      setSaveState('saving');
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => flush(), SAVE_DEBOUNCE_MS);
    },
    [flush]
  );

  // Retry unsent edits as soon as the connection comes back.
  useEffect(() => {
    if (connection === 'open' && pendingRef.current) flush();
  }, [connection, flush]);

  const hasCoaches = hasCoachData(session);

  // Config echoes from matching-server.
  useEffect(() => {
    if (!session.config) return;
    const json = JSON.stringify(session.config);
    if (json === lastSentRef.current) {
      if (!pendingRef.current) setSaveState('saved');
      return;
    }
    if (pendingRef.current) {
      setRemoteChanged(true);
      return;
    }
    lastSentRef.current = json;
    dispatch({
      type: 'replace',
      config: fitConfigToSession(session.config, hasCoaches),
    });
    setSaveState('saved');
  }, [session.config, hasCoaches]);

  const flushRef = useRef(flush);
  useLayoutEffect(() => {
    flushRef.current = flush;
  }, [flush]);

  // Send unsent edits when the setup unmounts (the admin navigates away).
  // React runs every layout-effect cleanup before any useEffect cleanup, and
  // useMatchingSession closes the socket in a useEffect cleanup on the parent,
  // so the socket is still open here. A plain useEffect cleanup would run
  // after the parent's and find the socket closed. flush() also clears the
  // pending timer.
  useLayoutEffect(() => () => void flushRef.current(), []);

  // A reload or a closed tab doesn't unmount React, so also try on pagehide.
  // Best effort: the browser may tear the socket down before the send leaves.
  useEffect(() => {
    const onPageHide = () => void flushRef.current();
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, []);

  const dismissRemoteChanged = useCallback(() => setRemoteChanged(false), []);

  return {
    config,
    update,
    flush,
    saveState,
    remoteChanged,
    dismissRemoteChanged,
  };
}

export type MatchingDraft = ReturnType<typeof useMatchingDraft>;
